import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

import { Box, Title, Text, Divider, SimpleGrid, MultiSelect, Group,Button, Grid, Flex, Skeleton, Rating, Anchor, Card, Tooltip, Alert } from "@mantine/core";
import { IconDownload, IconReportSearch, IconTrendingDown, IconTrendingUp, IconUserPin } from "@tabler/icons-react";

import { useDownloadEvaluacionesPdf } from "../hooks/useDownloadEvaluacionesPDF";
import useActiveProcess from "../hooks/useActiveProcess.js";
import useActivePermission from "../hooks/useActivePermission.js";
import { GetAcademicUnitUserPositions, GetAllSchoolCareers } from "../actions/academicActions";
import { getAllCriteriosResume } from "../actions/overviewActions";
import { SaveEvaluationReview, DeleteEvaluationReview } from "../actions/evaluationActions";

import AcademicUnitUserInfo from "../components/Overview/AcademicUnitUserInfo";
import ImprovementPlanModal from "../components/ModalImprovementPlans";
import ModalCareersRatings from "../components/Overview/ModalCareersRatings";
import MetricWithActionButton from "../components/Overview/MetricWithActionButton";
import ModalRatingAndText from '../components/ModalRatingAndText'
import AttributeGroup from "../components/Overview/AttributeGroup";
import ModalUnitEvidences from "../components/Overview/ModalUnitEvidences";
import ModalCriteriaRatings from "../components/Overview/ModalCriteriaRatings";
import ButtonFeedback from "../components/ButtonFeedback.js";

import { Position } from "../enums/PermissionsEnums";
import { ATTRIBUTE_TYPES } from "../enums/AttributesTypes";
import { isFacultyUnit, isRectoriaUnit} from "../utils/permissionHelpers.js";
import { isProcessActive } from "../utils/processHelpers.js";
import { MessagePlus } from "tabler-icons-react";

const setConfigData = (unitId) => {
  const isFaculty = /^[A-Z]$/.test(unitId);
  const config = {
    unitId: unitId,
    unitType: isFaculty ? 'f' : 'c',
    title: isFaculty ? 'Facultad' : 'Carrera',
    inCharge: isFaculty ? 'Decano' : 'Director',
  }

  return config;
};

const UnitDetailPage = () => {
  
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { data: dataAcademicInfo, loading: loadingAcademicInfo} = useSelector((state) => state.academic.unitUsersInfo)
  const { data: dataSchoolCareers, loading: loadingSchoolCareers } = useSelector((state) => state.academic.schoolCareers)
  const { data: dataCriterios, evaluacionGeneral: dataGeneralEvaluation, loading: loadingCriterios } = useSelector((state) => state.overview.resumeCriterias)
  const { saving: savingReview, loading: loadingReview } = useSelector((state) => state.evaluation.reviews)

  const activePermission = useActivePermission();
  const activeProcess = useActiveProcess();
  const isEditionEnabled = isProcessActive(activeProcess);
  const { downloadPdf, downloadPreviousPdf, isDownloading, error } = useDownloadEvaluacionesPdf();

  const [searchParams] = useSearchParams();
  const sede = searchParams.get('sede');

  const { unitId } = useParams();
  const unitConfig = useMemo(() => setConfigData(unitId), [unitId]);

  const memoizedCriterios = useMemo(() => dataCriterios || [], [dataCriterios]);
  const uniqueCategories = useMemo(() => {
    const categories = memoizedCriterios.flatMap((criterio) => criterio.categoria || []);
    const unique = Array.from(new Set(categories.map((cat) => cat.nombre))).map((nombre) => {
      const category = categories.find((cat) => cat.nombre === nombre);
      return {
        value: category.Id.toUpperCase(),
        label: category.nombre,
      };
    });
    return unique;
  }, [memoizedCriterios]);
  const memoizedGeneralEvObservation = useMemo(() => dataGeneralEvaluation?.observacion || null, [dataGeneralEvaluation]);

  const [selectedSede, setSelectedSede] = useState(null);
  const [availableSedes, setAvailableSedes] = useState([]);
  const [filters, setFilters] = useState({
    criterios: [],
    categorias: [],
    cualidades: [],
  });
  const [modalImprovePlanOpened, setModalImprovePlanOpened] = useState(false); 
  const [mainPositionUser, setMainPositionUser] = useState({});
  const [otherPositionUsers, setOtherPositionUsers] = useState([]);
  const [schoolDetail, setSchoolDetail] = useState([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);

  const [modalCareersOpened, setModalCareersOpened] = useState(false);
  const [careersRatings, setCareersRatings] = useState([]);

  const [modalEvidencesOpened, setModalEvidencesOpened] = useState(false);
  const [selectedEvidences, setSelectedEvidences] = useState([]);

  const [modalReviewOpened, setModalReviewOpened] = useState(false);
  const [reviewEvaluationId, setReviewEvaluationId] = useState(null)
  const [reviewValues, setReviewValues] = useState(null)

  const [modalCriteriaRatingsOpened, setModalCriteriaRatingsOpened] = useState(false);
  const [criteriaRatings, setCriteriaRatings] = useState([]);

  const isRectoria = isRectoriaUnit(activePermission);
  const isFaculty = isFacultyUnit(activePermission)

  useEffect(() => {
    dispatch(GetAcademicUnitUserPositions(unitConfig.unitId, user));
    
    const schoolUnit = unitConfig.unitType === 'f' ? unitId : unitId.charAt(0);
    dispatch(GetAllSchoolCareers(schoolUnit, user));
  }, [unitConfig]);

  useEffect(() => {
    if (sede && availableSedes.length > 0) {
      handleSedeChange(sede);
    }
  }, [sede, availableSedes]);

  useEffect(() => {
    if (dataAcademicInfo) {
      
      if (unitConfig.unitType === 'f') {
        const cargos = Object.values(dataAcademicInfo['GEN']?.cargos || {});

        const decanoCargo = cargos?.find(cargo => cargo.Id.toUpperCase() === Position.DECANO);
        const mainPosition = decanoCargo?.usuarios[0] || {};

        const otherPositions = cargos?.filter(cargo => cargo.Id.toUpperCase() !== Position.DECANO);

        setMainPositionUser(mainPosition);
        setOtherPositionUsers(otherPositions);

      }else if (unitConfig.unitType === 'c') {
        const sedes = Object.keys(dataAcademicInfo).map((codigo) => ({
          value: codigo,
          label: dataAcademicInfo[codigo].descripcion,
        })); 

        setAvailableSedes(sedes);
        handleSedeChange(sedes[0]?.value); 
        
      }
    }

  },[dataAcademicInfo, unitConfig]);

  
  useEffect(() => {
    if (dataSchoolCareers.length > 0) {
      if (unitConfig.unitType === 'f'){
        const school = dataSchoolCareers[0];
        setSchoolDetail(school);

        handleFetchEvaluations(unitConfig.unitId, null, null, null);
      }else if(unitConfig.unitType === 'c'){
        if (!selectedSede) return;

        const school = dataSchoolCareers[0];
        const filteredCarreras = school.carreras?.filter((carrera) => carrera.codCarrera.toUpperCase() === unitConfig.unitId);
        setSchoolDetail({ ...school, carreras: filteredCarreras });

        handleFetchEvaluations(unitConfig.unitId, selectedSede, null, null);
      }
    }
  }, [dataSchoolCareers,selectedSede, unitConfig]);

  const handleFetchEvaluations = (unitId, sede) => {
    dispatch(getAllCriteriosResume(activeProcess.Id, unitId, sede, user));
  };

  const handleSedeChange = (sede) => {
    setSelectedSede(sede);

    const cargos = Object.values(dataAcademicInfo[sede]?.cargos || {});

    const directorCargo = cargos?.find(cargo => cargo.Id.toUpperCase() === Position.DIRECTOR);
    const mainPosition = directorCargo?.usuarios[0] || {};

    const otherPositions = cargos?.filter(cargo => cargo.Id.toUpperCase() !== Position.DIRECTOR);

    setMainPositionUser(mainPosition);
    setOtherPositionUsers(otherPositions);
  }
 
  const handleShowImprovementPlan = (evaluationId) => {
    setSelectedEvaluation(evaluationId);
    setModalImprovePlanOpened(true);  
  };
  const handleCloseImprovementPlan = () => {
    setModalImprovePlanOpened(false);  
    setSelectedEvaluation(null);
  }

  const handleShowEvidences = (data) => {
    setSelectedEvidences(data);
    setModalEvidencesOpened(true);  
  };
  const handleCloseEvidences = () => {
    setSelectedEvidences(null);
    setModalEvidencesOpened(false);  
  }

  const handleShowCareersRatings = (valoraciones) => {
    if (!schoolDetail?.carreras) return;
  
    const combinedCareers = schoolDetail.carreras.flatMap((carrera) => {
      const valoracionesCarrera = valoraciones?.filter((v) => v.codigoCarrera === carrera.codCarrera) || [];
  
      if (valoracionesCarrera.length === 0) {
        return {
          codigo: carrera.codCarrera || "Desconocido",
          descripcion: carrera.descripcion || "Carrera no encontrada",
          sede: "Sin sede",
          puntuacion: 0,
          detalle: "Sin comentarios",
        };
      }

      return valoracionesCarrera.map((valoracion) => ({
        codigo: carrera.codCarrera || "Desconocido",
        descripcion: carrera.descripcion || "Carrera no encontrada",
        sede: valoracion?.codigoSede,
        puntuacion: valoracion.puntuacion || 0,
        detalle: valoracion.comentarios || "Sin comentarios",
      }));
    });
  
    setCareersRatings(combinedCareers);
    setModalCareersOpened(true);
  };
  const handleCloseCareersRatings = () => {
    setModalCareersOpened(false);
    setCareersRatings([]);
  };

  const filteredCriterios = useMemo(() => {
    return memoizedCriterios.filter((criterio) => {
      const { criterios, categorias, cualidades } = filters;

      if (criterios.length && !criterios.includes(criterio.numero)) {
        return false;
      }

      if ( categorias.length && !criterio.categoria?.some((cat) => categorias.includes(cat.Id.toUpperCase()))) {
        return false;
      }

      if ( cualidades.length && !criterio.evaluacion?.cualidades?.some((c) => cualidades.includes(c.tipoAtributo.Id.toUpperCase()))) {
        return false;
      }

      return true;
    });
  }, [memoizedCriterios, filters]);


  const handleShowReviewModal = (evaluationId,review) => {
    const obj = {
      Id: review?.Id ?? null,
      puntuacion: review?.puntuacion ?? 0,
      comentarios: review?.comentarios ?? ''
    }
    setReviewEvaluationId(evaluationId);
    setReviewValues(obj);

    setModalReviewOpened(true);
  };

  const handleCloseReviewModal = () => {
    setReviewValues(null);
    setModalReviewOpened(false);
  }

  const refreshEvaluations = () => {
    const sede = unitConfig.unitType === 'c' ? selectedSede : null;
    handleFetchEvaluations(unitConfig.unitId, sede);
  };

  const handleSaveReview = (values) => {

    if(values && reviewEvaluationId){
      const rev = {
        puntuacion: values.puntuacion,
        comentarios: values.comentarios
      };

      if(values.Id) rev.Id = values.Id;
      
      dispatch(SaveEvaluationReview(reviewEvaluationId, rev, user)).then(() => {
        handleCloseReviewModal();
        refreshEvaluations();
      });
    }
    
  };

  const handleDeleteReview = (id) => {
    if (id){
      dispatch(DeleteEvaluationReview(id, user)).then(() => {
        handleCloseReviewModal();
        refreshEvaluations();
      });
    }
  };

  const handleShowCriteriaRatings = (data) => {
    setCriteriaRatings(data);
    setModalCriteriaRatingsOpened(true);  
  };
  const handleCloseCriteriaRatings  = () => {
    setCriteriaRatings([]);
    setModalCriteriaRatingsOpened(false);  
  }
  
  const handleDownloadPDF = () => {
    const sede = selectedSede || null;
    downloadPdf(activeProcess.Id,unitConfig.unitId, sede);
  };

  const handleDownloadPreviousPDF = () => {
    const sede = selectedSede || null;
    downloadPreviousPdf(activeProcess.Id, unitConfig.unitId, sede);
  };

  const CriteriaTable = ({data,unitType}) => {
    
    return (
      <>
        {data?.map((criterio, index) => {
        const totalValoraciones = criterio.evaluacion?.valoraciones?.length || 0;
        const valoracionesAprobadas = criterio.evaluacion?.valoraciones?.filter(v => v.puntuacion >= 3).length || 0;
        const porcentajeAprobadas = totalValoraciones > 0 ? ((valoracionesAprobadas / totalValoraciones) * 100).toFixed(2) : 0;

        const countEvidencias = String(criterio.evidencias?.length || 0).padStart(2, "0");

        const countDebilidades = criterio.evaluacion?.cualidades?.filter(c => c.tipoAtributo.Id.toUpperCase() === ATTRIBUTE_TYPES.DEBILIDAD)?.length || 0;
        const countFortalezas = criterio.evaluacion?.cualidades?.filter(c => c.tipoAtributo.Id.toUpperCase() === ATTRIBUTE_TYPES.FORTALEZA)?.length || 0;
          
        const countPlanesMejora = String(criterio.evaluacion?.planMejoras?.length || 0).padStart(2, "0");

        const reviewRectoriaUser = criterio.evaluacion?.revisiones?.find((rev) => rev.vc_insuser === user.userid);
        const reviewGeneralScore = criterio.evaluacion?.valoraciones?.length > 0 
          ? (criterio.evaluacion.valoraciones.reduce((sum, v) => sum + (v.puntuacion || 0), 0) / criterio.evaluacion.valoraciones.length).toFixed(2)
          : 0;

        return (
          <Box key={index} className="container-pink">
            <Grid justify="space-between" align="center" grow>
              <Grid.Col span={2.5}>
                <Text fw={600}>Criterio {criterio.numero}</Text>
                <Text size="sm" c={"dimmed"}>{criterio.descripcion}</Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <MetricWithActionButton
                  label={countEvidencias}
                  buttonLabel={'Ver Evidencias'}
                  onClick={() => handleShowEvidences(criterio.evidencias)}
                />
              </Grid.Col>
              {unitType === 'f' && (
                <Grid.Col span={2}>
                  <MetricWithActionButton
                    label={`${porcentajeAprobadas}%`}
                    buttonLabel={'Ver carreras'}
                    onClick={() => handleShowCareersRatings(criterio.evaluacion?.valoraciones)}
                  />
                </Grid.Col>
              )}
              <Grid.Col span={2}>
                <Flex justify="center" align="center" direction="column" wrap="nowrap" gap="xs">
                <AttributeGroup
                  label="Debilidades"
                  count={countDebilidades}
                  attributes={criterio.evaluacion?.cualidades?.filter(
                    (c) => c.tipoAtributo.Id.toUpperCase() === ATTRIBUTE_TYPES.DEBILIDAD
                  )}
                  badgeIcon={<IconTrendingDown color="red" size={15}/>}
                />
                <AttributeGroup
                  label="Fortalezas"
                  count={countFortalezas}
                  attributes={criterio.evaluacion?.cualidades?.filter(
                    (c) => c.tipoAtributo.Id.toUpperCase() === ATTRIBUTE_TYPES.FORTALEZA
                  )}
                  badgeIcon={<IconTrendingUp color="green" size={15}/>}
                />
                </Flex>
              </Grid.Col>
              <Grid.Col span={2}>
                <MetricWithActionButton
                  label={countPlanesMejora}
                  buttonLabel={'Ver Plan de Mejora'}
                  onClick={() => handleShowImprovementPlan(criterio.evaluacion?.Id)}
                />
              </Grid.Col>
              {(isRectoria || isFaculty) && (
                <Grid.Col span={1.5}>
                  <Flex justify="center" align="center" direction="column" wrap="nowrap">
                    {isRectoria ? (
                      <>
                        {reviewRectoriaUser?.Id && (
                          <Tooltip label='Mi valoración' withArrow>
                            <Box>
                              <Rating value={reviewRectoriaUser.puntuacion} size="sm" count={4} readOnly mb={'md'}/>
                            </Box>
                          </Tooltip>
                        )}
                        {isEditionEnabled && (
                          <Button 
                            color='#E72166'
                            mb={'xs'}
                            w={'80%'}
                            onClick={() => handleShowReviewModal(criterio.evaluacion?.Id, reviewRectoriaUser )}
                            disabled={!criterio.evaluacion?.Id}
                          >
                            {reviewRectoriaUser?.Id ? 'Editar' : 'Valorar'}
                          </Button>
                        )}
                      </>
                    ) : (
                      isFaculty && (
                        <Tooltip label="Valoración promedio" withArrow>
                          <Box>
                            <Rating value={reviewGeneralScore} size="sm" count={4} readOnly mb={'md'}/>
                          </Box>
                        </Tooltip>
                      )
                    )}
                    {(criterio.evaluacion?.Id || criterio.evaluacion?.revisiones?.length > 0) ? (
                      <Anchor size={'xs'} ta={'center'} onClick={() => handleShowCriteriaRatings(criterio.evaluacion?.revisiones)}>
                        Ver valoraciones
                      </Anchor>
                    ) : (
                      <Text size={'xs'} c="dimmed" ta={'center'}>
                        Sin valoraciones
                      </Text>
                    )}
                  </Flex>
                </Grid.Col>
              )}
            </Grid>
          </Box>
        );
      })}
      </>
    )
  }
  
  return (
    <div>
      <Box className="card-base" mb={'md'}>
        <Group justify="space-between" wrap="nowrap">
          <Title order={2} fw={400}>
            Informacion del {unitConfig.inCharge}
          </Title>
          <IconUserPin size={28}/>
        </Group>
        <Divider my="xs" size={'sm'}/>
        <AcademicUnitUserInfo
          mainPosition={mainPositionUser}
          otherPositions={otherPositionUsers}
          schoolDetail={schoolDetail}
          careersDetails={schoolDetail?.carreras || []}
          sedesDetails={availableSedes}
          selectedSede={selectedSede}
          onSedeChange={handleSedeChange}
          loadingState={(loadingAcademicInfo || loadingSchoolCareers)}
        />
      </Box>
      {unitConfig.unitType === 'f' && ( isFaculty ||  isRectoria) && (activeProcess.Id.toUpperCase() == '2B28DDD2-708C-4D6D-ADC9-1A3B2F7C6D7E') && (
        <Card withBorder radius="md" p="md" mb={'md'} style={{ background: 'linear-gradient(50deg, #eef5fcff 20%, #c5e3ffff 100%)', borderColor: '#228be6' }}>
          <Flex>
            <Text flex={1}>
              Se encuentra disponible el <b>documento de retroalimentación general</b> sobre la evaluación
            </Text>
            <ButtonFeedback codFaculty={unitConfig.unitId}/>
          </Flex>
        </Card>
      )}
      <Box className="card-base">
        <section style={{ marginBottom: '2.5rem' }}>
          <Group justify="space-between" wrap="nowrap">
            <Title order={2} fw={400}>
              Análisis de Criterios
            </Title>
            <Group justify="space-between" wrap="nowrap">
              {isEditionEnabled && (
                <Button
                  leftSection={<IconDownload size={20} />}
                  variant="default"
                  size="xs"
                  loading={isDownloading}
                  onClick={() => handleDownloadPreviousPDF()}
                >
                 Exportar PDF anterior 
                </Button>
              )}
              <Button 
                leftSection= {<IconDownload size={20}/>}
                color={'#E72166'}
                size="xs"
                loading={isDownloading}
                onClick={() => handleDownloadPDF()}
              >
                Exportar PDF actual 
              </Button>
              <IconReportSearch size={28}/>
            </Group>
          </Group>
          <Divider my="xs" size={'sm'}/>
          <Text c={'dimmed'} mb={'sm'}>
            Utilizá los filtros por criterio, función o tipo de resultado para analizar en detalle el desempeño de las carreras evaluadas. Además, podés descargar un archivo PDF con el detalle completo de los criterios y sus evaluaciones para su consulta o impresión.
          </Text>
          <Text c={'dimmed'} mb={'lg'}>
            El promedio de valoración se expresa como el porcentaje de valoraciones iguales o superiores a 3 estrellas respecto del total de valoraciones registradas.
          </Text>
          { memoizedGeneralEvObservation && (
            <Alert variant="outline" color="gray.7" radius="md" title="Observaciones Generales sobre los planes de mejora" icon={<MessagePlus />}>
              {memoizedGeneralEvObservation ?? 'No se han registrado observaciones'}
            </Alert>
          )}
        </section>
        <section>
          <Box mb={'lg'}>
            <SimpleGrid cols={3}>
              <div>
                <MultiSelect
                  label="Criterios"
                  placeholder="Selecciona uno o mas criterios"
                  clearable
                  checkIconPosition="right"
                  comboboxProps={{ shadow: 'md' }}
                  data={memoizedCriterios.map((criterio) => ({
                    value: criterio.numero,
                    label: `Criterio ${criterio.numero}`,
                  }))}
                  onChange={(values) => setFilters((prev) => ({ ...prev, criterios: values }))}
                />
              </div>
              <div>
                <MultiSelect
                  label="Funciones"
                  placeholder="Selecciona uno o mas funciones"
                  clearable
                  checkIconPosition="right"
                  comboboxProps={{ shadow: 'md' }}
                  data={uniqueCategories}
                  onChange={(values) => setFilters((prev) => ({ ...prev, categorias: values }))}
                />
              </div>
              <div>
                <MultiSelect
                  label="Debilidades/Fortalezas"
                  placeholder="Selecciona uno o mas cualidades"
                  clearable
                  checkIconPosition="right"
                  comboboxProps={{ shadow: 'md' }}
                  data={[
                    { value: ATTRIBUTE_TYPES.DEBILIDAD, label: "Debilidades" },
                    { value: ATTRIBUTE_TYPES.FORTALEZA, label: "Fortalezas" },
                  ]}
                  onChange={(values) => setFilters((prev) => ({ ...prev, cualidades: values }))}
                />
              </div>
            </SimpleGrid>
          </Box>
          <Box>
            <Box className="container-pink">
              <Grid grow>
                <Grid.Col span={2.5}>
                  <Title ta={"center"} order={3} fw={400} c={"#E72166"}>
                    Criterios
                  </Title>
                </Grid.Col>
                <Grid.Col span={2}>
                  <Title ta={"center"} order={3} fw={400} c={"#E72166"}>
                    Evidencias
                  </Title>
                </Grid.Col>
                {unitConfig.unitType === 'f' && (
                  <Grid.Col span={2}>
                    <Title ta={"center"} order={3} fw={400} c={"#E72166"}>
                      Promedio de valoracion
                    </Title>
                  </Grid.Col>
                )}
                <Grid.Col span={2}>
                  <Title ta={"center"} order={3} fw={400} c={"#E72166"}>
                    Cualidades
                  </Title>
                </Grid.Col>
                <Grid.Col span={2}>
                  <Title ta={"center"} order={3} fw={400} c={"#E72166"}>
                    Plan de Mejoras
                  </Title>
                </Grid.Col>
                {(isRectoria || isFaculty) && (
                  <Grid.Col span={1.5}>
                    <Title ta={"center"} order={3} fw={400} c={"#E72166"}>
                      Valoración
                    </Title>
                  </Grid.Col>
                )}
              </Grid>
            </Box>
            <Box>
              {loadingCriterios /*&& (!dataCriterios || dataCriterios.length === 0)*/ ? (
                <Skeleton height={120} mt={6} radius="md" />
              ) : filteredCriterios.length === 0 ? (
                <Text size={'sm'} c="dimmed" ta={"center"}>No se encontraron criterios</Text>
              ) : (
                <CriteriaTable data={filteredCriterios} unitType={unitConfig.unitType} />
              )}
            </Box>
          </Box>
        </section>
      </Box>

      <ImprovementPlanModal
        opened={modalImprovePlanOpened}
        onClose={handleCloseImprovementPlan}
        idEvaluacion={selectedEvaluation}
      />
      <ModalCareersRatings
        opened={modalCareersOpened}
        onClose={handleCloseCareersRatings}
        careers={careersRatings}
      />
      <ModalUnitEvidences
        opened={modalEvidencesOpened}
        onClose={handleCloseEvidences}
        evidences={selectedEvidences}
      />
      <ModalRatingAndText
        opened={modalReviewOpened}
        onClose={handleCloseReviewModal}
        onSave={handleSaveReview}
        onDelete={handleDeleteReview}
        initialValues={reviewValues}
        title="Valoración de criterio"
        saveLabel="Guardar valoración"
        deleteLabel="Eliminar valoración"
        saveLoading={savingReview}
        deleteLoading={loadingReview}
      />
      <ModalCriteriaRatings
        opened={modalCriteriaRatingsOpened}
        onClose={handleCloseCriteriaRatings}
        ratings={criteriaRatings}
      />
    </div>
  )
}

export default UnitDetailPage;